import { motion } from "framer-motion";
import { ShieldAlert, HeartPulse, Scale, Truck, FileWarning, Gavel } from "lucide-react";
import Seo from "@/components/Seo";
import { Footer } from "@/components/Footer";

const SECTIONS = [
    {
        icon: ShieldAlert,
        title: "1. Live Arrival Guarantee",
        body: [
            "We guarantee that every live animal will arrive alive when shipped via our approved overnight air freight service. To honor this guarantee, the buyer (or an authorized adult) must be present to receive the shipment on the first delivery attempt and must follow the acclimation instructions provided.",
            "If an animal arrives deceased (DOA), the buyer must notify us within two (2) hours of the recorded delivery time, with clear photo and video evidence of the animal in its unopened shipping container. Verified DOA claims are resolved with a replacement animal or store credit at our discretion.",
        ],
    },
    {
        icon: HeartPulse,
        title: "2. Buyer Responsibility After Arrival",
        body: [
            "Once an animal has arrived alive and has been accepted by the buyer, full and sole responsibility for the animal transfers to the buyer. If an animal becomes ill, is injured, or dies at any point after arrival — for any reason — the buyer is responsible. No refunds, replacements, credits, or compensation of any kind will be issued for animals that die after a confirmed live arrival.",
            "By purchasing, you confirm that you have researched the species, prepared an appropriate enclosure with correct temperature, humidity, lighting, and diet before the animal arrives, and accept that reptiles and exotic animals are living creatures whose long-term wellbeing depends entirely on the keeper.",
        ],
    },
    {
        icon: Truck,
        title: "3. Shipping & Freight",
        body: [
            "Live animals ship overnight via air freight in climate-appropriate, insulated packaging with heat or cold packs as conditions require. We may delay shipment at our sole discretion when temperatures along the route are unsafe; the live arrival guarantee remains in effect only when we approve the shipping window.",
            "Freight costs are quoted after checkout based on destination and are payable before dispatch. Delivery dates are estimates, not guarantees.",
        ],
    },
    {
        icon: FileWarning,
        title: "4. Venomous & Restricted Species",
        body: [
            "Venomous and otherwise restricted species are sold only to verified, experienced keepers of legal age. We may require proof of experience, secure housing, and any permits required by your jurisdiction before completing a sale, and we reserve the right to cancel and refund any order that cannot be verified.",
            "The buyer is solely responsible for knowing and obeying all federal, state, and local laws governing the possession, transport, and keeping of the purchased animal.",
        ],
    },
    {
        icon: Scale,
        title: "5. Orders, Pricing & Payment",
        body: [
            "All orders are subject to acceptance and availability. Prices are listed in USD and may change without notice. Payment is processed securely by Stripe; we never see or store your card details.",
            "Because live animals are perishable goods, all sales are final once an order has shipped. Orders cancelled before dispatch may be refunded minus any non-recoverable processing fees.",
        ],
    },
    {
        icon: Gavel,
        title: "6. Limitation of Liability",
        body: [
            "To the fullest extent permitted by law, Logan's Reptiles & Exotics is not liable for any injury, illness, loss, damage, or death — to persons, property, or animals — arising from the keeping, handling, or husbandry of any animal or use of any product after delivery.",
            "Our total liability for any claim relating to a purchase is limited to the purchase price of the animal or product in question.",
        ],
    },
];

export default function TermsPage() {
    return (
        <main className="pt-28" data-testid="terms-page">
            <Seo
                title="Terms of Service — Logan's Reptiles & Exotics"
                siteName="Logan's Reptiles & Exotics"
                description="Terms of service: live arrival guarantee conditions, buyer responsibility after arrival, shipping, restricted species verification, and liability for purchases from Logan's Reptiles & Exotics."
            />
            <div className="mx-auto max-w-4xl px-6 sm:px-12">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="font-mono2 text-xs uppercase tracking-[0.35em] text-red-500"
                >
                    The Fine Print
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="mt-4 font-display text-4xl font-extrabold tracking-tight text-bone sm:text-5xl"
                >
                    Terms of <span className="font-editorial italic text-red-500">Service</span>
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.25 }}
                    className="mt-6 text-base leading-relaxed text-ash"
                >
                    These terms govern every purchase from Logan's Reptiles &amp; Exotics. Live
                    animals are not ordinary goods — read this carefully before checkout. Completing a
                    purchase constitutes acceptance of these terms in full.
                </motion.p>

                <div
                    data-testid="terms-key-policy"
                    className="mt-10 border border-red-800/60 bg-red-950/20 p-6"
                >
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-red-400">
                        Key policy — read first
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-bone">
                        We guarantee <span className="text-red-400">live arrival only</span>. If an
                        animal dies at any point after it has arrived alive and been accepted, the{" "}
                        <span className="text-red-400">buyer is fully responsible</span> — no refunds,
                        replacements, or credits will be issued after a confirmed live arrival.
                    </p>
                </div>

                <div className="mt-14 space-y-10 pb-24">
                    {SECTIONS.map((s, i) => (
                        <motion.section
                            key={s.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.6, delay: i * 0.05 }}
                            data-testid={`terms-section-${i + 1}`}
                            className="border-l-2 border-red-900/60 pl-6"
                        >
                            <h2 className="flex items-center gap-3 font-display text-lg font-bold text-bone sm:text-xl">
                                <s.icon className="h-5 w-5 shrink-0 text-red-600" />
                                {s.title}
                            </h2>
                            {s.body.map((p, j) => (
                                <p key={j} className="mt-4 text-sm leading-relaxed text-ash">
                                    {p}
                                </p>
                            ))}
                        </motion.section>
                    ))}
                </div>
            </div>
            <Footer />
        </main>
    );
}
